import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { GridColDef } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import { CommonDatagrid } from '..';
import { AuditLogService } from '../../../api/services/auditLogService';
import { AdminUsersService } from '../../../api/services/adminUsersService';
import { setLogs, setPagination } from '../../../store/Reducers/AuditLogSlice';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import {
  IAdminAuditLogDetailResponse,
  IAdminAuditLogResponse,
} from '../../../types/api/response/auditlogresponse';
import { IAdminUser, INavigationContent, IRootState } from '../../../types/store';
import { AuditLogDetailDialog } from './AuditLogDetailDialog';
import { NavigationService } from '../../../api/services/navigationService';
import { setNavigationPages } from '../../../store/Reducers/NavigationSlice';

interface FlatNav {
  id: number;
  title: string;
  parentTitle: string | null;
  fullPath: string;
}

const flattenNavigations = (navs: INavigationContent[]): FlatNav[] =>
  navs.flatMap((nav) => [
    { id: nav.id, title: nav.title, parentTitle: null, fullPath: nav.title },
    ...(nav.children || []).map((child) => ({
      id: child.id,
      title: child.title,
      parentTitle: nav.title,
      fullPath: `${nav.title} > ${child.title}`,
    })),
  ]);

const HTTP_METHOD_MAP: Record<string, { label: string; color: string }> = {
  GET: { label: 'View', color: 'info' },
  POST: { label: 'Create', color: 'success' },
  PUT: { label: 'Update', color: 'warning' },
  PATCH: { label: 'Update', color: 'warning' },
  DELETE: { label: 'Delete', color: 'error' },
};

const MethodChip = ({ value }: { value: string }) => {
  const mapped = HTTP_METHOD_MAP[value?.toUpperCase()] ?? { label: value, color: 'default' };
  return <Chip label={mapped.label} color={mapped.color as any} size="small" />;
};

const StatusChip = ({ value }: { value: number }) => {
  const color =
    value >= 500 ? 'error' : value >= 400 ? 'warning' : value >= 200 ? 'success' : 'default';
  return <Chip label={value} color={color as any} size="small" />;
};

export const AuditLogDatagrid = () => {
  const dispatch = useDispatch();
  const { logs, totalElements, pagination } = useSelector((state: IRootState) => state.auditLog);
  const navigations = useSelector((state: IRootState) => state.navigations.content);

  const [selectedNav, setSelectedNav] = useState<FlatNav | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<IAdminUser[]>([]);
  const [adminUsers, setAdminUsers] = useState<IAdminUser[]>([]);
  const [detailLog, setDetailLog] = useState<IAdminAuditLogDetailResponse | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [expandedParentId, setExpandedParentId] = useState<number | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const keepOpenRef = useRef(false);

  const [startDate, setStartDate] = useState<Dayjs | null>(
    dayjs().subtract(7, 'day').startOf('day'),
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs().endOf('day'));
  const [dateError, setDateError] = useState('');

  const flatNavs = useMemo(() => flattenNavigations(navigations), [navigations]);

  const visibleNavOptions = useMemo(() => {
    const result: FlatNav[] = [];
    for (const parent of flatNavs.filter((n) => n.parentTitle === null)) {
      result.push(parent);
      if (parent.id === expandedParentId) {
        result.push(...flatNavs.filter((n) => n.parentTitle === parent.title));
      }
    }
    return result;
  }, [flatNavs, expandedParentId]);

  useEffect(() => {
    AdminUsersService.getAdminUsers().then((res) => setAdminUsers(res.content));
  }, []);

  useEffect(() => {
    if (navigations.length === 0) {
      NavigationService.getAllNavigations().then((res) => dispatch(setNavigationPages(res)));
    }
  }, [navigations.length, dispatch]);

  const validateDates = (start: Dayjs | null, end: Dayjs | null): string => {
    if (start && end) {
      if (end.isBefore(start)) return 'Start date must not be after end date';
      if (end.diff(start, 'day') > 7) return 'Date range cannot exceed 7 days';
    }
    return '';
  };

  const fetchLogs = useCallback(
    async (
      page = 0,
      size = pagination.size,
      nav = selectedNav,
      users = selectedUsers,
      start = startDate,
      end = endDate,
    ) => {
      try {
        const res = await AuditLogService.getAuditLogs({
          ...(nav ? { navigation_id: nav.id, include_children: true } : {}),
          ...(users.length > 0 ? { user_id: users.map((u) => u.username) } : {}),
          ...(start ? { start_date: start.format('YYYY-MM-DDTHH:mm:ss') } : {}),
          ...(end ? { end_date: end.format('YYYY-MM-DDTHH:mm:ss') } : {}),
          page,
          size,
          sort: 'timestamp,desc',
        });
        dispatch(setLogs({ content: res.content, totalElements: res.page.totalElements }));
        dispatch(setPagination({ page: res.page.number, size: res.page.size }));
      } catch {
        dispatch(showNotification({ message: 'Failed to fetch audit logs.', severity: 'error' }));
      }
    },
    [dispatch, pagination.size, selectedNav, selectedUsers, startDate, endDate],
  );

  useEffect(() => {
    fetchLogs(
      0,
      pagination.size,
      null,
      [],
      dayjs().subtract(7, 'day').startOf('day'),
      dayjs().endOf('day'),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = async (id: number) => {
    setLoadingDetail(true);
    setDetailOpen(true);
    try {
      const detail = await AuditLogService.getAuditLogDetail(id);
      setDetailLog(detail);
    } catch {
      dispatch(showNotification({ message: 'Failed to load log detail.', severity: 'error' }));
      setDetailOpen(false);
    } finally {
      setLoadingDetail(false);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'timestamp',
      headerName: 'Timestamp',
      flex: 1.5,
      valueFormatter: (value: string) => (value ? new Date(value).toLocaleString() : '—'),
    },
    { field: 'user_id', headerName: 'User', flex: 1, valueFormatter: (v: string) => v ?? '—' },
    {
      field: 'http_method',
      headerName: 'Method',
      width: 110,
      renderCell: ({ value }) => <MethodChip value={value} />,
    },
    { field: 'url_path', headerName: 'URL Path', flex: 2 },
    {
      field: 'status_code',
      headerName: 'Status',
      width: 100,
      renderCell: ({ value }) => <StatusChip value={value} />,
    },
    { field: 'execution_time_ms', headerName: 'Time (ms)', width: 110 },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: ({ row }: { row: IAdminAuditLogResponse }) => (
        <Tooltip title="View details">
          <IconButton size="small" onClick={() => openDetail(row.id)}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const filterBar = (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-end" mb={2}>
        <Autocomplete
          options={visibleNavOptions}
          getOptionLabel={(o) => o.fullPath}
          value={selectedNav}
          open={navOpen}
          onOpen={() => setNavOpen(true)}
          onClose={(_) => {
            if (keepOpenRef.current) {
              keepOpenRef.current = false;
              return;
            }
            setNavOpen(false);
          }}
          onChange={(_, val) => {
            if (!val) {
              setSelectedNav(null);
              setExpandedParentId(null);
              return;
            }
            setSelectedNav(val);
            setExpandedParentId(null);
            setNavOpen(false);
          }}
          filterOptions={(x) => x}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          sx={{ minWidth: 280 }}
          renderOption={(props, option) => {
            const hasChildren = flatNavs.some((n) => n.parentTitle === option.title);
            const isExpanded = expandedParentId === option.id;
            const isParent = option.parentTitle === null;
            return (
              <li {...props} key={option.id}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                  sx={{
                    pl: isParent ? 0 : 3,
                    fontWeight: isParent ? 600 : 400,
                    fontSize: isParent ? 14 : 13,
                    color: isParent ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {option.title}
                  {hasChildren && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        keepOpenRef.current = true;
                        setExpandedParentId((prev) => (prev === option.id ? null : option.id));
                      }}
                    >
                      {isExpanded ? (
                        <KeyboardArrowUpIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      ) : (
                        <KeyboardArrowDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                      )}
                    </IconButton>
                  )}
                </Box>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Navigation (optional)"
              size="small"
              placeholder="All navigations"
            />
          )}
        />
        <Autocomplete
          multiple
          options={adminUsers}
          getOptionLabel={(u) => `${u.first_name} ${u.last_name} (${u.username})`}
          value={selectedUsers}
          onChange={(_, val) => setSelectedUsers(val)}
          sx={{ minWidth: 280 }}
          renderInput={(params) => (
            <TextField {...params} label="Users (optional)" size="small" placeholder="All users" />
          )}
        />
        <DatePicker
          label="From"
          value={startDate}
          onChange={(val) => {
            setStartDate(val);
            setDateError(validateDates(val, endDate));
          }}
          slotProps={{ textField: { size: 'small', sx: { width: 175 } } }}
        />
        <DatePicker
          label="To"
          value={endDate}
          onChange={(val) => {
            setEndDate(val);
            setDateError(validateDates(startDate, val));
          }}
          slotProps={{ textField: { size: 'small', sx: { width: 175 } } }}
        />
        {dateError && (
          <Typography variant="caption" color="error" sx={{ width: '100%', mt: -1 }}>
            {dateError}
          </Typography>
        )}
        <Button
          variant="contained"
          onClick={() =>
            fetchLogs(0, pagination.size, selectedNav, selectedUsers, startDate, endDate)
          }
          disabled={!!dateError}
          sx={{ fontSize: 12, fontWeight: 600, height: 40 }}
        >
          Apply Filters
        </Button>
        <Button
          variant="outlined"
          onClick={() => {
            const s = dayjs().subtract(7, 'day').startOf('day');
            const e = dayjs().endOf('day');
            setSelectedNav(null);
            setSelectedUsers([]);
            setExpandedParentId(null);
            setStartDate(s);
            setEndDate(e);
            setDateError('');
            fetchLogs(0, pagination.size, null, [], s, e);
          }}
          sx={{ fontSize: 12, height: 40 }}
        >
          Clear
        </Button>
      </Box>
    </LocalizationProvider>
  );

  return (
    <>
      <CommonDatagrid
        header={filterBar}
        data={logs}
        columns={columns}
        rowId="id"
        serverPagination
        page={pagination.page}
        pageSize={pagination.size}
        rowCount={totalElements}
        onPageChange={(p) => fetchLogs(p, pagination.size)}
        onPageSizeChange={(s) => fetchLogs(0, s)}
        isCellEditable={() => false}
      />
      <AuditLogDetailDialog
        log={detailLog}
        isOpen={detailOpen && !loadingDetail}
        onClose={() => {
          setDetailOpen(false);
          setDetailLog(null);
        }}
      />
    </>
  );
};
